import type { SkipReason } from './auto-link-types.js';

/**
 * The one vendor value that enables Auto Link. Auto Link is an Iris-only,
 * Electron-only behavior: Iris auto-links the bridge on boot; Hive (ptzoptics)
 * pairs manually over mDNS and MUST NOT auto-link; an unknown vendor MUST NOT
 * auto-link. See knowledge: hive-no-auto-link-bootstrap-contract
 * (ai.knowledge/platform) and vendor-is-runtime-not-build-time.
 */
export const AUTO_LINK_VENDOR = 'iris';

export interface AutoLinkVendorInput {
  /** Whether this is the Electron app — Auto Link never runs on the web build. */
  readonly isElectron: boolean;
  /**
   * The runtime-resolved vendor (`config.vendor`). Configuration now rejects
   * unresolved identity, but this action gate still treats stale, contradictory,
   * or directly supplied values as untrusted and accepts only exact Iris.
   */
  readonly vendor: string | undefined;
}

/** Skip reasons this decision can produce; both are observable terminal reasons. */
export type AutoLinkVendorSkipReason = Extract<SkipReason, 'not_electron' | 'not_iris_vendor'>;

export type AutoLinkVendorDecision =
  | { readonly eligible: true }
  | { readonly eligible: false; readonly reason: AutoLinkVendorSkipReason };

/**
 * SINGLE SOURCE of the frontend Auto Link vendor decision (ENG-4367). Every
 * renderer enforcement point — auto-link-guard bypass, controller eligibility,
 * auto-link-room org-pin, and the identity-store unlinked-settle gate — routes
 * through this helper. Electron main separately validates the build-baked
 * identity at its IPC trust boundary before contacting the sidecar.
 *
 * Fail-closed by construction: anything that is not exactly an Electron +
 * `'iris'` pairing returns `eligible: false`, so Hive / unknown / missing /
 * empty / garbage vendor never activates Auto Link. The distinct
 * `not_electron` and `not_iris_vendor` reasons are preserved so the skip stays
 * observable in analytics + the state machine.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global) — fail-closed
 * vendor resolution; unknown vendor never auto-links.
 */
export const evaluateAutoLinkVendor = ({
  isElectron,
  vendor
}: AutoLinkVendorInput): AutoLinkVendorDecision => {
  if (!isElectron) {
    return { eligible: false, reason: 'not_electron' };
  }

  // Exact match ONLY — never a normalized or trimmed match. This independently
  // protects the action boundary if stale or garbage state reaches it after boot.
  if (vendor !== AUTO_LINK_VENDOR) {
    return { eligible: false, reason: 'not_iris_vendor' };
  }

  return { eligible: true };
};

/** Boolean convenience wrapper over {@link evaluateAutoLinkVendor}. */
export const shouldAutoLink = (input: AutoLinkVendorInput): boolean =>
  evaluateAutoLinkVendor(input).eligible;
